import React from "react";
import Header from "../Header";
import Footer from "../Footer";
import SEO from "../SEO";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  StethoscopeIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  CheckCircleIcon,
  PhoneIcon,
  CalendarIcon,
  HeartIcon,
  ShieldIcon,
  AwardIcon,
  AlertTriangleIcon,
  LightbulbIcon,
  ActivityIcon,
  ZapIcon
} from "lucide-react";
import BookAppointmentModal from "../BookAppointmentModal";
import Breadcrumb from "../Breadcrumb";
import { openWhatsApp } from "../../lib/utils";

const Hernia = () => {
  const breadcrumbItems = [
    { label: "Home", href: "/" },
    { label: "Hernia Treatment", href: "/hernia" }
  ];

  const stats = [
    { number: "97%", label: "Success Rate", icon: ShieldIcon },
    { number: "45min", label: "Procedure Time", icon: ClockIcon },
    { number: "2-3 Weeks", label: "Recovery Time", icon: ActivityIcon },
    { number: "700+", label: "Successful Cases", icon: AwardIcon }
  ];

  const causes = [
    "Weakness in abdominal wall muscles",
    "Heavy lifting and physical strain",
    "Chronic coughing or sneezing",
    "Obesity and excessive weight gain",
    "Pregnancy and childbirth",
    "Previous abdominal surgery",
    "Age-related muscle weakening",
    "Genetic predisposition"
  ];

  const symptoms = [
    "Visible bulge or lump in abdomen or groin",
    "Pain or discomfort at hernia site",
    "Heavy or dragging sensation",
    "Pain that worsens with coughing or lifting",
    "Nausea and vomiting (if strangulated)",
    "Constipation or difficulty with bowel movements",
    "Burning or aching sensation",
    "Weakness or pressure in groin area"
  ];

  const procedures = [
    {
      name: "Laparoscopic Hernia Repair",
      description: "Minimally invasive repair using small incisions and mesh",
      duration: "45-60 minutes",
      recovery: "1-2 weeks",
      benefits: ["Smaller incisions", "Less post-op pain", "Faster recovery", "Lower recurrence rate"]
    },
    {
      name: "Open Hernia Repair",
      description: "Traditional surgical repair with direct access",
      duration: "30-45 minutes",
      recovery: "2-3 weeks",
      benefits: ["Proven technique", "Cost-effective", "High success rate", "Suitable for all hernias"]
    },
    {
      name: "Tension-Free Mesh Repair",
      description: "Modern approach using synthetic mesh reinforcement",
      duration: "30-45 minutes",
      recovery: "2-3 weeks",
      benefits: ["Stronger repair", "Lower recurrence", "Better long-term results", "Reduced tension"]
    }
  ];

  const prevention = [
    "Maintain healthy weight and avoid obesity",
    "Use proper lifting techniques",
    "Strengthen core muscles with exercise",
    "Quit smoking to reduce coughing",
    "Treat chronic constipation",
    "Avoid heavy lifting when possible",
    "Eat high-fiber diet for regular bowel movements",
    "Wear supportive garments if needed"
  ];

  const diagnosis = [
    "Physical examination and medical history",
    "Visual inspection for bulges or lumps",
    "Cough test to detect hernia protrusion",
    "Ultrasound imaging of affected area",
    "CT scan for detailed assessment",
    "MRI for complex hernias",
    "Blood tests for overall health",
    "Assessment of hernia size and type"
  ];

  const herniaStructuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    "name": "Hernia Treatment",
    "description": "Advanced hernia repair using minimally invasive laparoscopic techniques. Expert care for hernia treatment with modern surgical methods and comprehensive recovery support.",
    "procedureType": "Surgical Treatment",
    "bodyLocation": "Abdomen and Groin",
    "preparation": "Pre-surgical consultation and preparation",
    "followup": "Post-operative care and recovery monitoring",
    "provider": {
      "@type": "MedicalOrganization",
      "name": "TheLivCure",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Delhi",
        "addressCountry": "IN"
      }
    },
    "medicalSpecialty": "General Surgery",
    "areaServed": ["Delhi", "Gurgaon", "Noida", "Faridabad"]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Hernia Treatment"
        description="Expert hernia repair at TheLivCure. Advanced minimally invasive laparoscopic hernia surgery. Experienced surgeons, modern facilities, 97% success rate. Book consultation today."
        keywords="hernia treatment, hernia surgery, laparoscopic hernia repair, hernia operation Delhi, hernia doctor, hernia specialist, inguinal hernia, umbilical hernia, hernia treatment cost"
        url="/hernia"
        structuredData={herniaStructuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 via-white to-teal-50 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mt-8">
            <div>
              <Badge className="bg-teal-100 text-teal-800 mb-4">General Surgery</Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Advanced Hernia Treatment
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-6 leading-relaxed">
                Expert hernia repair with minimally invasive laparoscopic techniques and 
                advanced mesh repair methods. Get relief from hernia pain and prevent 
                complications with our comprehensive surgical approach.
              </p>
              <p className="text-base text-gray-500 mb-8 leading-relaxed">
                A hernia occurs when an organ or fatty tissue pushes through a weak spot in surrounding muscle or connective tissue. 
                Our experienced general surgeons use the latest laparoscopic and open surgical techniques to repair hernias effectively, 
                reducing pain and preventing recurrence with advanced mesh repair methods.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <BookAppointmentModal>
                  <Button className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-3 text-lg">
                    Book Free Consultation
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </BookAppointmentModal>
                <Button 
                  variant="outline" 
                  className="border-teal-600 text-teal-600 hover:bg-teal-50 px-8 py-3 text-lg"
                  onClick={() => openWhatsApp("9311720045", "Hi, I need consultation for hernia treatment")}
                >
                  <PhoneIcon className="w-5 h-5 mr-2" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-400 to-teal-600 rounded-2xl p-8 text-white mb-8">
                <div className="grid grid-cols-2 gap-6">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <stat.icon className="w-8 h-8 mx-auto mb-2" />
                      <div className="text-2xl sm:text-3xl font-bold">{stat.number}</div>
                      <div className="text-teal-100 text-sm">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    src="/image/hernia_1.jpg" 
                    alt="Hernia repair surgery procedure" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-3 text-center">
                  Advanced laparoscopic hernia repair procedure
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Hernias Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding Hernias</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Hernias are common conditions that occur when an organ or fatty tissue squeezes through 
                a weak spot in a surrounding muscle or connective tissue called fascia. They can develop 
                in various parts of the body, with the most common types being inguinal, femoral, umbilical, and incisional hernias.
              </p>
              <p className="text-base text-gray-500 mb-6 leading-relaxed">
                While some hernias may cause no symptoms, others can lead to severe pain and complications 
                if left untreated. Early diagnosis and treatment are essential to prevent serious complications 
                such as strangulation, where the blood supply to the herniated tissue is cut off.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Inguinal Hernia</h3>
                    <p className="text-gray-600">Most common type, occurs in the groin area</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Umbilical Hernia</h3>
                    <p className="text-gray-600">Occurs near the belly button, common in infants and adults</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Incisional Hernia</h3>
                    <p className="text-gray-600">Develops at the site of a previous surgical incision</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/hernia_2.jpg" 
                alt="Hernia anatomy and types" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Understanding hernia anatomy and types
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms and Warning Signs Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recognizing Hernia Symptoms</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Hernia symptoms can range from mild discomfort to severe pain. Early recognition 
              and treatment can prevent complications and improve quality of life.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HeartIcon className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Visible Bulge</h3>
              <p className="text-sm text-gray-600">Noticeable lump or swelling in affected area</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangleIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Pain & Discomfort</h3>
              <p className="text-sm text-gray-600">Aching or burning sensation at hernia site</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ActivityIcon className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Heavy Sensation</h3>
              <p className="text-sm text-gray-600">Feeling of heaviness or dragging in affected area</p>
            </Card>
            
            <Card className="text-center p-6 hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Activity Worsens</h3>
              <p className="text-sm text-gray-600">Pain increases with lifting, coughing, or straining</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Advanced Surgical Techniques Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Surgical Techniques</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Our general surgery team employs the most advanced hernia repair techniques, 
                including laparoscopic surgery and tension-free mesh repair, to ensure optimal 
                outcomes with minimal pain and faster recovery.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ZapIcon className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Laparoscopic Repair</h3>
                    <p className="text-gray-600">Minimally invasive surgery with smaller incisions and faster recovery</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <ShieldIcon className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Mesh Repair</h3>
                    <p className="text-gray-600">Advanced synthetic mesh reinforcement for stronger repair</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <AwardIcon className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Open Surgery</h3>
                    <p className="text-gray-600">Traditional approach for complex hernias with proven effectiveness</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8">
              <img 
                src="/image/hernia_3.jpg" 
                alt="Advanced hernia repair equipment" 
                className="w-full h-64 object-cover rounded-lg"
              />
              <p className="text-sm text-gray-600 mt-3 text-center">
                Modern hernia repair surgical equipment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="causes">Causes</TabsTrigger>
              <TabsTrigger value="symptoms">Symptoms</TabsTrigger>
              <TabsTrigger value="diagnosis">Diagnosis</TabsTrigger>
              <TabsTrigger value="procedures">Procedures</TabsTrigger>
              <TabsTrigger value="prevention">Prevention</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <StethoscopeIcon className="w-6 h-6 mr-2 text-teal-600" />
                    Hernia Treatment Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-6">
                    A hernia occurs when an organ or fatty tissue pushes through a weak spot 
                    in surrounding muscle or connective tissue. Our advanced treatment options 
                    include laparoscopic repair, open surgery, and tension-free mesh repair 
                    for optimal outcomes.
                  </p>
                  <p className="text-gray-600 mb-6">
                    Hernias are common conditions that can develop in various parts of the body, most commonly in the abdomen and groin. 
                    Our experienced general surgeons use the latest minimally invasive techniques to repair hernias effectively, 
                    reducing pain and preventing recurrence.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="text-center p-6 bg-teal-50 rounded-lg">
                      <ClockIcon className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                      <h3 className="font-semibold mb-2">Quick Recovery</h3>
                      <p className="text-sm text-gray-600">Most patients return to normal activities within 2-3 weeks</p>
                    </div>
                    <div className="text-center p-6 bg-teal-50 rounded-lg">
                      <ShieldIcon className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                      <h3 className="font-semibold mb-2">High Success Rate</h3>
                      <p className="text-sm text-gray-600">97% success rate with our advanced surgical techniques</p>
                    </div>
                    <div className="text-center p-6 bg-teal-50 rounded-lg">
                      <ZapIcon className="w-8 h-8 text-teal-600 mx-auto mb-2" />
                      <h3 className="font-semibold mb-2">Minimally Invasive</h3>
                      <p className="text-sm text-gray-600">Laparoscopic techniques for faster healing</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="causes" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertTriangleIcon className="w-6 h-6 mr-2 text-orange-600" />
                    Common Causes of Hernia
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {causes.map((cause, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-teal-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{cause}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="symptoms" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <HeartIcon className="w-6 h-6 mr-2 text-red-600" />
                    Symptoms to Watch For
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {symptoms.map((symptom, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{symptom}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="diagnosis" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <ActivityIcon className="w-6 h-6 mr-2 text-blue-600" />
                    Diagnostic Methods
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {diagnosis.map((test, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{test}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="procedures" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {procedures.map((procedure, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg">{procedure.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">{procedure.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Duration:</span>
                          <span className="text-sm font-medium">{procedure.duration}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Recovery:</span>
                          <span className="text-sm font-medium">{procedure.recovery}</span>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium mb-2">Benefits:</h4>
                        <ul className="space-y-1">
                          {procedure.benefits.map((benefit, idx) => (
                            <li key={idx} className="flex items-start text-sm">
                              <CheckCircleIcon className="w-4 h-4 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="prevention" className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <LightbulbIcon className="w-6 h-6 mr-2 text-yellow-600" />
                    Prevention Tips
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {prevention.map((tip, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircleIcon className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{tip}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Hernia Treatment Gallery</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See our advanced hernia repair facilities and procedures in action. 
              Our modern equipment and expert care ensure the best outcomes for our patients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/hernia_1.jpg" 
                alt="Hernia repair procedure overview" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Treatment Overview</h3>
                <p className="text-sm text-gray-600">Advanced minimally invasive hernia repair procedures</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/hernia_2.jpg" 
                alt="Hernia anatomy and diagnosis" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Anatomy & Diagnosis</h3>
                <p className="text-sm text-gray-600">Understanding hernia anatomy and types</p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <img 
                src="/image/hernia_3.jpg" 
                alt="Modern surgical equipment" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Modern Equipment</h3>
                <p className="text-sm text-gray-600">State-of-the-art hernia repair surgical equipment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-teal-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Get Relief from Hernia?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            Book your consultation today and take the first step towards a pain-free life.
          </p>
          <BookAppointmentModal>
            <Button className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-3 text-lg">
              Book Free Consultation
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </BookAppointmentModal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Hernia;
